import * as THREE from 'three';
import type { Ember, FlowData, EmberSprite } from '@/types';
import { CONFIG } from '@/config';
import { store } from '@/state';
import { getThreatColorRGB } from '@/utils/colors';

/** Cached ember texture */
let emberTexture: THREE.CanvasTexture | null = null;

/**
 * Create the ember particle texture
 * @returns Canvas texture for ember sprites
 */
function createEmberTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;

  const centerX = 64;
  const centerY = 64;

  // Outer glow
  const outerGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 64);
  outerGlow.addColorStop(0, 'rgba(255, 100, 0, 0)');
  outerGlow.addColorStop(0.4, 'rgba(255, 50, 0, 0.1)');
  outerGlow.addColorStop(0.7, 'rgba(200, 0, 0, 0.05)');
  outerGlow.addColorStop(1, 'rgba(100, 0, 0, 0)');
  ctx.fillStyle = outerGlow;
  ctx.fillRect(0, 0, 128, 128);

  // Middle glow
  const middleGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 45);
  middleGlow.addColorStop(0, 'rgba(255, 200, 100, 0.8)');
  middleGlow.addColorStop(0.3, 'rgba(255, 150, 50, 0.6)');
  middleGlow.addColorStop(0.6, 'rgba(255, 80, 0, 0.3)');
  middleGlow.addColorStop(1, 'rgba(255, 0, 0, 0)');
  ctx.fillStyle = middleGlow;
  ctx.fillRect(0, 0, 128, 128);

  // Core
  const core = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 20);
  core.addColorStop(0, 'rgba(255, 255, 255, 1)');
  core.addColorStop(0.2, 'rgba(255, 255, 200, 0.9)');
  core.addColorStop(0.5, 'rgba(255, 200, 100, 0.6)');
  core.addColorStop(0.8, 'rgba(255, 150, 50, 0.3)');
  core.addColorStop(1, 'rgba(255, 100, 0, 0)');
  ctx.fillStyle = core;
  ctx.fillRect(0, 0, 128, 128);

  return new THREE.CanvasTexture(canvas);
}

/**
 * Get or create the ember texture
 * @returns Cached ember texture
 */
export function getEmberTexture(): THREE.CanvasTexture {
  if (!emberTexture) {
    emberTexture = createEmberTexture();
  }
  return emberTexture;
}

/**
 * Create a Three.js sprite object for an ember
 * @param data - Ember data
 * @returns Three.js Sprite
 */
export function createEmberObject(data: Ember): THREE.Sprite {
  const texture = getEmberTexture();

  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    blending: THREE.AdditiveBlending,
    transparent: true,
    depthWrite: false,
    opacity: data.opacity,
  });

  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.scale.set(data.size, data.size, 1);

  const colorRGB = data.colorRGB;
  sprite.material.color.setRGB(colorRGB.r / 255, colorRGB.g / 255, colorRGB.b / 255);

  sprite.userData = {
    createdAt: data.createdAt,
    initialSize: data.size,
    flickerOffset: data.flickerOffset,
  };

  return sprite;
}

/**
 * Add an ember (hot spot) from a flow
 * @param flow - Flow data
 */
export function addEmber(flow: FlowData): void {
  const { dstLat, dstLng, mbps, threat } = flow;
  const state = store.getState();

  // Check if threat level is enabled
  if (!store.isThreatLevelEnabled(Math.floor(threat) as 0 | 1 | 2 | 3 | 4 | 5)) {
    return;
  }

  const size = Math.min(8, 2 + (mbps / 100) * 6);
  const colorRGB = getThreatColorRGB(threat);

  const ember: Ember = {
    lat: dstLat,
    lng: dstLng,
    size,
    colorRGB,
    opacity: 1.0,
    createdAt: Date.now(),
    initialSize: size,
    mbps,
    threat,
    flickerOffset: Math.random() * Math.PI * 2,
  };

  const embers = state.embers;

  if (embers.length < CONFIG.maxEmbers) {
    embers.push(ember);
  } else {
    const index = store.nextEmberIndex();
    embers[index] = ember;
  }

  store.set('embersDirty', true);
}

/**
 * Update all embers (fade, shrink, remove expired)
 */
export function updateEmbers(): void {
  const state = store.getState();
  const now = Date.now();
  let writeIndex = 0;
  let bandwidth = 0;

  const embers = state.embers;

  for (let i = 0; i < embers.length; i++) {
    const ember = embers[i];
    const age = now - ember.createdAt;

    // Remove expired embers
    if (age > CONFIG.emberLifetime) {
      continue;
    }

    // Update opacity and size
    const fadeProgress = age / CONFIG.emberLifetime;
    ember.opacity = 1.0 - Math.pow(fadeProgress, 1.5);
    const shrinkage = 1 - fadeProgress * 0.4;
    ember.size = ember.initialSize * shrinkage;

    embers[writeIndex] = ember;
    writeIndex++;
    bandwidth += ember.mbps;
  }

  // Compact the array if needed
  if (writeIndex !== embers.length) {
    embers.length = writeIndex;
    store.set('embersDirty', true);
  }

  // Update bandwidth stat
  state.stats.currentBandwidth = bandwidth;

  // Update globe if dirty
  if (state.embersDirty) {
    const globe = state.globe;
    if (globe) {
      globe.objectsData(embers);
    }
    store.set('embersDirty', false);
  }
}

/**
 * Update sprite properties (flicker, pulse effects)
 */
export function updateSpriteProperties(): void {
  const state = store.getState();
  const globe = state.globe;

  if (!globe) return;

  const now = Date.now();

  globe.scene().traverse((object: THREE.Object3D) => {
    if (object instanceof THREE.Sprite && object.userData.createdAt) {
      const sprite = object as EmberSprite;
      const age = now - sprite.userData.createdAt;
      const fadeProgress = age / CONFIG.emberLifetime;

      // Flicker effect
      const flickerSpeed = 0.003;
      const flickerAmount = 0.15;
      const flicker =
        1 +
        Math.sin(age * flickerSpeed + sprite.userData.flickerOffset) *
          flickerAmount *
          (1 - fadeProgress);

      // Pulse effect
      const pulseSpeed = 0.002;
      const pulseAmount = 0.08;
      const pulse = 1 + Math.sin(age * pulseSpeed) * pulseAmount * (1 - fadeProgress * 0.5);

      // Apply opacity with flicker
      const opacity = 1.0 - Math.pow(fadeProgress, 1.5);
      sprite.material.opacity = opacity * flicker;

      // Apply size with pulse
      const shrinkage = 1 - fadeProgress * 0.4;
      const finalSize = sprite.userData.initialSize * pulse * shrinkage;
      sprite.scale.set(finalSize, finalSize, 1);
    }
  });
}
